/**
 * Endpoints de **Gestión → Clientes → Créditos y vales**
 * (`/api/gestion/clientes/{creditos,vales,pagos}`).
 *
 * ⚠️ El **retiro** de galones baja el stock del producto, así que las mutaciones
 * invalidan también las claves de productos e inventario.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiClient } from '@/lib/http/api-client';

import type {
  CatalogosSaldos,
  ConsumoCredito,
  ConsumoVale,
  CreditoCliente,
  DatosConsumo,
  DatosCredito,
  DatosVale,
  PagosRespuesta,
  SaldosRespuesta,
  ValeCliente,
} from './clientes-saldos.types';

const CLAVE = 'gestion';

export interface FiltrosSaldos {
  cliente_id?: number | string;
  producto_id?: number | string;
  estado?: string;
  saldo?: string;
  search?: string;
}

export const saldosKeys = {
  catalogos: () => [CLAVE, 'clientes-saldos', 'catalogos'] as const,
  creditos: (filtros: FiltrosSaldos) => [CLAVE, 'clientes-saldos', 'creditos', filtros] as const,
  consumosCredito: (id: number | null) =>
    [CLAVE, 'clientes-saldos', 'creditos', id, 'consumos'] as const,
  vales: (filtros: FiltrosSaldos) => [CLAVE, 'clientes-saldos', 'vales', filtros] as const,
  consumosVale: (id: number | null) => [CLAVE, 'clientes-saldos', 'vales', id, 'consumos'] as const,
  pagos: (filtros: FiltrosSaldos) => [CLAVE, 'clientes-saldos', 'pagos', filtros] as const,
};

/** Cadena de consulta de un objeto de filtros. */
function cadenaDe(filtros: object): string {
  const parametros = new URLSearchParams();

  for (const [clave, valor] of Object.entries(filtros)) {
    if (valor !== undefined && valor !== null && valor !== '') {
      parametros.set(clave, String(valor));
    }
  }

  const cadena = parametros.toString();

  return cadena ? `?${cadena}` : '';
}

export const saldosApi = {
  catalogos: (signal?: AbortSignal): Promise<CatalogosSaldos> =>
    apiClient.get<CatalogosSaldos>('/api/gestion/clientes/saldos/catalogos', { signal }),

  creditos: (filtros: FiltrosSaldos, signal?: AbortSignal): Promise<SaldosRespuesta<CreditoCliente>> =>
    apiClient.get<SaldosRespuesta<CreditoCliente>>(
      `/api/gestion/clientes/creditos${cadenaDe(filtros)}`,
      { signal },
    ),

  consumosDeCredito: (id: number, signal?: AbortSignal): Promise<{ consumos: ConsumoCredito[] }> =>
    apiClient.get<{ consumos: ConsumoCredito[] }>(`/api/gestion/clientes/creditos/${id}/consumos`, {
      signal,
    }),

  configurarCredito: (datos: DatosCredito): Promise<{ credito_id: number; mensaje: string }> =>
    apiClient.post<{ credito_id: number; mensaje: string }>('/api/gestion/clientes/creditos', {
      body: datos,
    }),

  actualizarCredito: (
    id: number,
    datos: Partial<DatosCredito> & { estado?: number; cantidad_restante?: number; total_credito_restante?: number },
  ): Promise<{ mensaje: string }> =>
    apiClient.put<{ mensaje: string }>(`/api/gestion/clientes/creditos/${id}`, { body: datos }),

  registrarConsumo: (id: number, datos: DatosConsumo): Promise<{ mensaje: string; total: number }> =>
    apiClient.post<{ mensaje: string; total: number }>(
      `/api/gestion/clientes/creditos/${id}/consumos`,
      { body: datos },
    ),

  ajustarConsumo: (consumoId: number, galones: number): Promise<{ mensaje: string }> =>
    apiClient.put<{ mensaje: string }>(`/api/gestion/clientes/creditos/consumos/${consumoId}`, {
      body: { galones },
    }),

  pagarCredito: (
    id: number,
    monto: number,
    claveIdempotencia?: string,
  ): Promise<{ mensaje: string; restante: number }> =>
    apiClient.post<{ mensaje: string; restante: number }>(
      `/api/gestion/clientes/creditos/${id}/pagos`,
      { body: { monto, clave_idempotencia: claveIdempotencia } },
    ),

  finalizarCredito: (id: number, forzar = false): Promise<{ mensaje: string }> =>
    apiClient.post<{ mensaje: string }>(
      `/api/gestion/clientes/creditos/${id}/finalizar${forzar ? '?forzar=1' : ''}`,
      { body: {} },
    ),

  vales: (filtros: FiltrosSaldos, signal?: AbortSignal): Promise<SaldosRespuesta<ValeCliente>> =>
    apiClient.get<SaldosRespuesta<ValeCliente>>(`/api/gestion/clientes/vales${cadenaDe(filtros)}`, {
      signal,
    }),

  consumosDeVale: (id: number, signal?: AbortSignal): Promise<{ consumos: ConsumoVale[] }> =>
    apiClient.get<{ consumos: ConsumoVale[] }>(`/api/gestion/clientes/vales/${id}/consumos`, {
      signal,
    }),

  configurarVale: (datos: DatosVale): Promise<{ vale_id: number; mensaje: string }> =>
    apiClient.post<{ vale_id: number; mensaje: string }>('/api/gestion/clientes/vales', {
      body: datos,
    }),

  actualizarVale: (
    id: number,
    datos: Partial<DatosVale> & { estado?: number; galonaje_restante?: number },
  ): Promise<{ mensaje: string }> =>
    apiClient.put<{ mensaje: string }>(`/api/gestion/clientes/vales/${id}`, { body: datos }),

  registrarConsumoVale: (
    id: number,
    datos: { galones: number; numero_vale?: string },
  ): Promise<{ mensaje: string }> =>
    apiClient.post<{ mensaje: string }>(`/api/gestion/clientes/vales/${id}/consumos`, { body: datos }),

  pagarVale: (id: number): Promise<{ mensaje: string; total: number }> =>
    apiClient.post<{ mensaje: string; total: number }>(`/api/gestion/clientes/vales/${id}/pagos`, {
      body: {},
    }),

  finalizarVale: (id: number): Promise<{ mensaje: string }> =>
    apiClient.post<{ mensaje: string }>(`/api/gestion/clientes/vales/${id}/finalizar`, { body: {} }),

  pagos: (filtros: FiltrosSaldos, signal?: AbortSignal): Promise<PagosRespuesta> =>
    apiClient.get<PagosRespuesta>(`/api/gestion/clientes/pagos${cadenaDe(filtros)}`, { signal }),
};

/** Catálogos de clientes y productos. */
export function useCatalogosSaldos(activo = true) {
  return useQuery({
    queryKey: saldosKeys.catalogos(),
    queryFn: ({ signal }) => saldosApi.catalogos(signal),
    enabled: activo,
    staleTime: 60_000,
  });
}

/** Líneas de crédito del cliente. */
export function useCreditos(filtros: FiltrosSaldos) {
  return useQuery({
    queryKey: saldosKeys.creditos(filtros),
    queryFn: ({ signal }) => saldosApi.creditos(filtros, signal),
    staleTime: 15_000,
  });
}

/** Retiros de una línea de crédito. */
export function useConsumosDeCredito(id: number | null) {
  return useQuery({
    queryKey: saldosKeys.consumosCredito(id),
    queryFn: ({ signal }) => saldosApi.consumosDeCredito(id as number, signal),
    enabled: id !== null,
  });
}

/** Líneas de vale del cliente. */
export function useVales(filtros: FiltrosSaldos) {
  return useQuery({
    queryKey: saldosKeys.vales(filtros),
    queryFn: ({ signal }) => saldosApi.vales(filtros, signal),
    staleTime: 15_000,
  });
}

/** Consumos de un vale. */
export function useConsumosDeVale(id: number | null) {
  return useQuery({
    queryKey: saldosKeys.consumosVale(id),
    queryFn: ({ signal }) => saldosApi.consumosDeVale(id as number, signal),
    enabled: id !== null,
  });
}

/** Pagos registrados. */
export function usePagosSaldos(filtros: FiltrosSaldos) {
  return useQuery({
    queryKey: saldosKeys.pagos(filtros),
    queryFn: ({ signal }) => saldosApi.pagos(filtros, signal),
    staleTime: 15_000,
  });
}

/** Invalida todo lo que tocan los saldos (incluye stock y productos). */
function invalidar(cliente: ReturnType<typeof useQueryClient>) {
  void cliente.invalidateQueries({ queryKey: [CLAVE, 'clientes-saldos'] });
  void cliente.invalidateQueries({ queryKey: [CLAVE, 'productos'] });
  void cliente.invalidateQueries({ queryKey: [CLAVE, 'inventario'] });
}

/** Configura (o acumula) el crédito de un producto. */
export function useConfigurarCredito() {
  const cliente = useQueryClient();

  return useMutation({
    mutationFn: (datos: DatosCredito) => saldosApi.configurarCredito(datos),
    onSuccess: () => invalidar(cliente),
  });
}

/** Edita la línea de crédito. */
export function useActualizarCredito() {
  const cliente = useQueryClient();

  return useMutation({
    mutationFn: (datos: {
      id: number;
      cantidad_maxima?: number;
      cantidad_restante?: number;
      total_credito?: number;
      total_credito_restante?: number;
      precio_acordado?: number;
      estado?: number;
    }) => saldosApi.actualizarCredito(datos.id, datos),
    onSuccess: () => invalidar(cliente),
  });
}

/** Registra el retiro de galones. */
export function useRegistrarConsumo() {
  const cliente = useQueryClient();

  return useMutation({
    mutationFn: (datos: { id: number } & DatosConsumo) => saldosApi.registrarConsumo(datos.id, datos),
    onSuccess: () => invalidar(cliente),
  });
}

/** Corrige los galones de un retiro. */
export function useAjustarConsumo() {
  const cliente = useQueryClient();

  return useMutation({
    mutationFn: (datos: { consumoId: number; galones: number }) =>
      saldosApi.ajustarConsumo(datos.consumoId, datos.galones),
    onSuccess: () => invalidar(cliente),
  });
}

/** Registra un pago del crédito. */
export function usePagarCredito() {
  const cliente = useQueryClient();

  return useMutation({
    mutationFn: (datos: { id: number; monto: number; claveIdempotencia?: string }) =>
      saldosApi.pagarCredito(datos.id, datos.monto, datos.claveIdempotencia),
    onSuccess: () => invalidar(cliente),
  });
}

/** Cierra la línea de crédito. */
export function useFinalizarCredito() {
  const cliente = useQueryClient();

  return useMutation({
    mutationFn: (datos: { id: number; forzar?: boolean }) =>
      saldosApi.finalizarCredito(datos.id, datos.forzar),
    onSuccess: () => invalidar(cliente),
  });
}

/** Configura (o acumula) el vale de un producto. */
export function useConfigurarVale() {
  const cliente = useQueryClient();

  return useMutation({
    mutationFn: (datos: DatosVale) => saldosApi.configurarVale(datos),
    onSuccess: () => invalidar(cliente),
  });
}

/** Edita la línea de vale. */
export function useActualizarVale() {
  const cliente = useQueryClient();

  return useMutation({
    mutationFn: (datos: {
      id: number;
      galonaje_acordado?: number;
      galonaje_restante?: number;
      precio?: number;
      estado?: number;
    }) => saldosApi.actualizarVale(datos.id, datos),
    onSuccess: () => invalidar(cliente),
  });
}

/** Registra el consumo de un vale. */
export function useRegistrarConsumoVale() {
  const cliente = useQueryClient();

  return useMutation({
    mutationFn: (datos: { id: number; galones: number; numero_vale?: string }) =>
      saldosApi.registrarConsumoVale(datos.id, {
        galones: datos.galones,
        numero_vale: datos.numero_vale,
      }),
    onSuccess: () => invalidar(cliente),
  });
}

/** Liquida el vale. */
export function usePagarVale() {
  const cliente = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => saldosApi.pagarVale(id),
    onSuccess: () => invalidar(cliente),
  });
}

/** Cierra el vale. */
export function useFinalizarVale() {
  const cliente = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => saldosApi.finalizarVale(id),
    onSuccess: () => invalidar(cliente),
  });
}
